
import React, { Component } from 'react';

import logger from '../../logger';
import { safePlayMediaElement } from '../../playbackUtils';

/**
 * The type of the React {@code Component} props of {@link AudioTrack}.
 */
type Props = {

    /**
     * The value of the id attribute of the audio element.
     */
    id: string,


    /**
     * The audio track.
     */
    audioTrack: ?Object,

    /**
     * Used to determine the value of the autoplay attribute of the underlying
     * audio element.
     */
    autoPlay: boolean,

    /**
     * Represents muted property of the underlying audio element.
     */
    muted: ?Boolean,

    /**
     * Represents volume property of the underlying audio element.
     */
    volume: ?number,

    /**
     * A function that will be executed when the reference to the underlying audio element changes in order to report
     * the initial volume value.
     */
    onInitialVolumeSet: Function
};

/**
 * The React/Web {@link Component} which is similar to and wraps around {@code HTMLAudioElement}.
 */
export default class AudioTrack extends Component<Props> {
    /**
     * Reference to the HTML audio element, stored until the file is ready.
     */
    _ref: ?HTMLAudioElement;
    _isUnmounted: boolean;
    _autoplayBlockedLogged: boolean;
    _playRetryScheduled: boolean;

    /**
     * Default values for {@code AudioTrack} component's properties.
     *
     * @static
     */
    static defaultProps = {
        autoPlay: true,
        id: ''
    };


    /**
     * Creates new <code>Audio</code> element instance with given props.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this._isUnmounted = false;
        this._autoplayBlockedLogged = false;
        this._playRetryScheduled = false;

        // Bind event handlers so they are only bound once for every instance.
        this._setRef = this._setRef.bind(this);
    }


    /**
     * Attaches the audio track to the audio element and plays it.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        this._isUnmounted = false;

        this._attachTrack(this.props.audioTrack);

        if (this._ref) {
            const { autoPlay, muted, volume } = this.props;

            if (autoPlay) {
                this._safePlayAudioElement();
            }

            if (typeof volume === 'number') {
                this._ref.volume = volume;
            }

            if (typeof muted === 'boolean') {
                this._ref.muted = muted;
            }
        }
    }

    /**
     * Remove any existing associations between the current audio track and the
     * component's audio element.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        this._isUnmounted = true;
        this._trackDetached = true; // Guard against double detach
        this._detachTrack(this.props.audioTrack);
    }

    /**
     * This component's updating is blackboxed from React to prevent re-rendering of the audio
     * element, as we set all the properties manually.
     *
     * @inheritdoc
     * @returns {boolean} - False is always returned to blackbox this component
     * from React.
     */
    shouldComponentUpdate(nextProps: Props) {
        const currentMHTrack = this.props.audioTrack?.mhTrack;
        const nextMHTrack = nextProps.audioTrack?.mhTrack;

        if (currentMHTrack !== nextMHTrack) {
            // Guard against double detach during component unmounting
            if (!this._trackDetached) {
                this._detachTrack(this.props.audioTrack);
            }
            this._trackDetached = false; // Reset for new track attachment
            this._attachTrack(nextProps.audioTrack);
        }

        if (this._ref) {
            const currentVolume = this._ref.volume;
            const nextVolume = nextProps.volume;

            if (typeof nextVolume === 'number' && !isNaN(nextVolume) && currentVolume !== nextVolume) {
                this._ref.volume = nextVolume;
            }

            const currentMuted = this._ref.muted;
            const nextMuted = nextProps.muted;

            if (typeof nextMuted === 'boolean' && currentMuted !== nextMuted) {
                this._ref.muted = nextMuted;
            }
        }

        return false;
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { autoPlay, id } = this.props;

        return (
            <audio
                autoPlay = { autoPlay }
                id = { id }
                ref = { this._setRef } />
        );
    }

    /**
     * Calls into the passed in track to associate the track with the component's audio element.
     *
     * @param {Object} track - The redux representation of the {@code MHLocalTrack}.
     * @private
     * @returns {void}
     */
    _attachTrack(track) {
        if (!track || !track.mhTrack) {
            return;
        }

        track?.mhTrack?.attach(this._ref);
    }

    /**
     * Removes the association to the component's audio element from the passed
     * in redux representation of jitsi audio track.
     *
     * @param {Object} track -  The redux representation of the {@code MHLocalTrack}.
     * @private
     * @returns {void}
     */
    _detachTrack(track) {
        if (this._ref && track && track.mhTrack) {
            track?.mhTrack?.detach(this._ref);
        }
    }

    _setRef: (?HTMLAudioElement) => void;

    /**
     * Sets the reference to the HTML audio element.
     *
     * @param {HTMLAudioElement} audioElement - The HTML audio element instance.
     * @private
     * @returns {void}
     */
    _setRef(audioElement: ?HTMLAudioElement) {
        this._ref = audioElement;
        const { onInitialVolumeSet } = this.props;

        if (this._ref && onInitialVolumeSet) {
            onInitialVolumeSet(this._ref.volume);
        }
    }

    /**
     * Calls play() safely and ignores expected interruptions during unmounts
     * or quick room/track transitions.
     *
     * @private
     * @returns {void}
     */
    _safePlayAudioElement() {
        const element = this._ref;

        if (!element || this._isUnmounted) {
            return;
        }

        // The audio element can be momentarily detached from the DOM when the
        // local video container is moved and re-attached. Retry once on the
        // next frame so the explicit play() is not permanently skipped.
        if (!element.isConnected) {
            this._schedulePlayRetry();

            return;
        }

        safePlayMediaElement(element, {
            onExpectedInterruption: error => {
                if (this._isExpectedPlayInterruption(error)) {
                    return;
                }

                logger.error('Audio play failed:', error);
            },
            onAutoplayBlocked: error => {
                if (!this._autoplayBlockedLogged) {
                    this._autoplayBlockedLogged = true;
                    logger.warn('Audio play blocked by browser policy:', error);
                }
            },
            onError: error => {
                logger.error('Audio play failed:', error);
            }
        });
    }

    /**
     * Returns true for benign play() failures caused by lifecycle timing.
     *
     * @param {Object} error - The play() rejection error.
     * @private
     * @returns {boolean}
     */
    _isExpectedPlayInterruption(error) {
        const message = error?.message || '';

        return this._isUnmounted
            || !this._ref
            || !this._ref.isConnected
            || error?.name === 'AbortError'
            || message.includes('play() request was interrupted')
            || message.includes('media was removed from the document');
    }

    /**
     * Schedules a single deferred play() attempt for the next frame. Used when
     * the element is temporarily detached during container reparenting. Only
     * one retry can be pending at a time so this can never loop.
     *
     * @private
     * @returns {void}
     */
    _schedulePlayRetry() {
        if (this._isUnmounted || this._playRetryScheduled) {
            return;
        }

        this._playRetryScheduled = true;

        const retry = () => {
            this._playRetryScheduled = false;

            if (this._isUnmounted) {
                return;
            }

            const element = this._ref;

            if (element && element.isConnected) {
                this._safePlayAudioElement();
            }
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(retry);
        } else {
            setTimeout(retry, 0);
        }
    }
}
